/**
 * The one rule the whole kit is built on.
 *
 * `base.css` carries a `prefers-reduced-motion` reset that flattens CSS
 * animation. It does nothing to `motion`, which writes **inline styles** — so a
 * `motion` element that starts at `opacity: 0` is invisible under reduced
 * motion, invisible with JavaScript disabled, and invisible in the prerendered
 * bytes a crawler reads. The media query cannot reach it.
 *
 * Two things follow, and they are both enforced rather than remembered:
 *
 *   1. Nothing that carries text animates from invisible. Entrances move on
 *      `transform` only; `opacity` stays at 1. That rule lives in `Reveal`.
 *   2. The reduced-motion answer must be known on the **first** render, not
 *      after an effect has run. `motion`'s own `useReducedMotion` starts as
 *      `false` and corrects itself, which means a reader who asked for less
 *      motion still sees the first animated frame. This starts from the media
 *      query synchronously and defaults to *reduced* when it cannot ask.
 */
import { useSyncExternalStore } from 'react'

const QUERY = '(prefers-reduced-motion: reduce)'

function subscribe(cb: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const list = window.matchMedia(QUERY)
  list.addEventListener?.('change', cb)
  return () => list.removeEventListener?.('change', cb)
}

function read(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return window.matchMedia(QUERY).matches
}

/**
 * Whether this reader has asked for less motion.
 *
 * The server snapshot is `true` — reduced — deliberately. The prerendered HTML
 * is one document served to everyone, so it must be the one that is correct for
 * the reader who cannot take movement: every figure at its resting state, every
 * word present. A reader who has not asked for less gets the animation after
 * hydration, which costs them nothing.
 */
export function useReducedMotionSafe(): boolean {
  return useSyncExternalStore(subscribe, read, () => true)
}
